import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Mail, Lock, Shield } from 'lucide-react'
import { toast } from 'sonner'
import { KaryvaLogoAnimated3D } from '../components/KaryvaLogoAnimated3D'
import { useAuth } from '../contexts/AuthContext'
import { apiErrorMessage } from '../utils/apiError'
import { apiClient } from '../services/api'

// Background blur elements
function BlurContainer1() {
  return (
    <div className="absolute bg-[#486581] blur-3xl filter right-[10%] rounded-full w-[384px] h-[384px] md:w-[500px] md:h-[500px] top-[10%] opacity-30" />
  )
}

function BlurContainer2() {
  return (
    <div className="absolute bg-[#DAA520] blur-3xl filter left-[5%] rounded-full w-[384px] h-[384px] md:w-[500px] md:h-[500px] top-[30%] opacity-30" />
  )
}

function BackgroundBlurs() {
  return (
    <div className="absolute inset-0 opacity-20 overflow-hidden">
      <BlurContainer1 />
      <BlurContainer2 />
    </div>
  )
}

// Feature Cards
interface FeatureCardProps {
  icon: React.ReactNode
  title: string
  description: string
  iconBgGradient: string
  shadowColor: string
}

function FeatureCard({ icon, title, description, iconBgGradient, shadowColor }: FeatureCardProps) {
  return (
    <div className="content-stretch flex gap-[12px] items-start">
      <div className={`relative rounded-[14px] ${shadowColor} shrink-0 size-[44px]`}>
        <div
          className={`${iconBgGradient} bg-clip-padding border-0 border-[transparent] border-solid box-border content-stretch flex items-center justify-center relative size-[44px] rounded-[14px]`}
        >
          {icon}
        </div>
      </div>
      <div className="basis-0 grow min-h-px min-w-px">
        <div className="bg-clip-padding border-0 border-[transparent] border-solid box-border content-stretch flex flex-col gap-[2px] items-start w-full">
          <p className="font-['Arial:Regular',sans-serif] leading-[20px] not-italic text-[14px] text-nowrap text-white whitespace-pre">
            {title}
          </p>
          <p className="font-['Arial:Regular',sans-serif] leading-[16px] not-italic text-[#90a1b9] text-[12px]">
            {description}
          </p>
        </div>
      </div>
    </div>
  )
}

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [mfaChallenge, setMfaChallenge] = useState<string | null>(null)
  const [mfaCode, setMfaCode] = useState('')
  const { login, verifyMfa } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      const result = await login(email, password)
      if (result.mfaRequired) {
        setMfaChallenge(result.challengeToken)
        setMfaCode('')
        return
      }
      toast.success('Login successful!')
      navigate('/')
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Login failed. Please check your credentials.'))
    } finally {
      setLoading(false)
    }
  }

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!mfaChallenge) return
    setLoading(true)
    try {
      await verifyMfa(mfaChallenge, mfaCode)
      toast.success('Login successful!')
      navigate('/')
    } catch (err) {
      const message = apiErrorMessage(err, 'That code didn’t work. Try again.')
      if (/expired/i.test(message)) {
        setMfaChallenge(null)
        toast.error('The sign-in took too long. Enter your password again.')
      } else {
        toast.error(message)
      }
    } finally {
      setLoading(false)
    }
  }

  // 2026-09-29: was a "coming soon" toast. Finds the workspace's identity
  // provider from the work email and hands the browser over to it.
  const handleSSOLogin = async () => {
    const address = email.trim()
    if (!address.includes('@')) {
      toast.error('Enter your work email first, then choose single sign-on.')
      return
    }
    setLoading(true)
    try {
      const { data } = await apiClient.post<{ sso: boolean; redirect_url?: string }>('/api/v1/sso/discover', { email: address })
      if (data.sso && data.redirect_url) {
        window.location.assign(data.redirect_url)
        return
      }
      toast.info(`Single sign-on isn't set up for ${address.split('@')[1]}. Sign in with your password.`)
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Couldn’t check single sign-on. Sign in with your password.'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen h-screen overflow-auto relative w-full bg-gradient-to-br from-[#102a43] via-[#1a3a52] to-[#102a43] flex flex-col">
      <BackgroundBlurs />

      {/* Main Content Container */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-8 md:py-12 relative z-10">
        <div className="w-full max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Side - Branding and Features */}
            <div className="w-full space-y-6 md:space-y-8">
              {/* Logo and Tagline */}
              <div className="space-y-4">
                <KaryvaLogoAnimated3D size="hero" darkBg={true} />
                <div className="space-y-2">
                  <p className="font-['Arial:Regular',sans-serif] leading-[28px] not-italic text-[#DAA520] text-lg md:text-xl">
                    Empowering you!
                  </p>
                  <p className="font-['Arial:Regular',sans-serif] leading-[20px] not-italic text-[#90a1b9] text-sm md:text-base">
                    Enterprise Project Management Office Suite
                  </p>
                </div>
              </div>

              {/* Heading and Description */}
              <div className="space-y-4">
                <p className="font-['Arial:Regular',sans-serif] leading-[32px] not-italic text-xl md:text-2xl lg:text-3xl text-white">
                  Intelligent Project Governance & Delivery
                </p>
                <p className="font-['Arial:Regular',sans-serif] leading-[26px] not-italic text-[#cad5e2] text-sm md:text-base lg:text-lg max-w-2xl">
                  Streamline your project portfolio with AI-powered insights, structured governance, and
                  real-time visibility across your entire organization.
                </p>
              </div>

              {/* Feature Cards */}
              <div className="w-full">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                <FeatureCard
                  icon={
                    <svg className="block size-5" fill="none" viewBox="0 0 20 20">
                      <path
                        d="M7.5 15H12.5"
                        stroke="white"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.66667"
                      />
                      <path
                        d="M8.33333 18.3333H11.6667"
                        stroke="white"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.66667"
                      />
                    </svg>
                  }
                  title="Intelligent Insights"
                  description="AI-powered decision support and predictive analytics"
                  iconBgGradient="bg-gradient-to-br from-blue-600 to-blue-700"
                  shadowColor="shadow-[0px_10px_15px_-3px_rgba(28,57,142,0.5),0px_4px_6px_-4px_rgba(28,57,142,0.5)]"
                />
                <FeatureCard
                  icon={
                    <svg className="block size-5" fill="none" viewBox="0 0 20 20">
                      <path
                        d="M5 2.5V12.5"
                        stroke="white"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.66667"
                      />
                      <path
                        d="M15 2.5V12.5"
                        stroke="white"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.66667"
                      />
                      <path
                        d="M10 2.5V12.5"
                        stroke="white"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.66667"
                      />
                      <path
                        d="M5 7.5H15"
                        stroke="white"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.66667"
                      />
                    </svg>
                  }
                  title="Structured Stage-Gate"
                  description="Proven G1-G5 methodology with approval workflows"
                  iconBgGradient="bg-gradient-to-br from-orange-600 to-orange-700"
                  shadowColor="shadow-[0px_10px_15px_-3px_rgba(123,51,6,0.5),0px_4px_6px_-4px_rgba(123,51,6,0.5)]"
                />
                <FeatureCard
                  icon={
                    <svg className="block size-5" fill="none" viewBox="0 0 20 20">
                      <path
                        d="M5 2.5V12.5"
                        stroke="white"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.66667"
                      />
                      <path
                        d="M15 2.5V12.5"
                        stroke="white"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.66667"
                      />
                      <path
                        d="M10 2.5V12.5"
                        stroke="white"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.66667"
                      />
                      <path
                        d="M5 7.5H15"
                        stroke="white"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.66667"
                      />
                    </svg>
                  }
                  title="PMO Governance"
                  description="Role-based access and compliance tracking"
                  iconBgGradient="bg-gradient-to-br from-slate-700 to-slate-800"
                  shadowColor="shadow-[0px_10px_15px_-3px_rgba(15,23,43,0.5),0px_4px_6px_-4px_rgba(15,23,43,0.5)]"
                />
                <FeatureCard
                  icon={
                    <svg className="block size-5" fill="none" viewBox="0 0 20 20">
                      <path
                        d="M15 14.1667V7.5"
                        stroke="white"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.66667"
                      />
                      <path
                        d="M10.8333 14.1667V4.16667"
                        stroke="white"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.66667"
                      />
                      <path
                        d="M6.66667 14.1667V11.6667"
                        stroke="white"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.66667"
                      />
                    </svg>
                  }
                  title="Real-time Reporting"
                  description="Live dashboards and executive insights"
                  iconBgGradient="bg-gradient-to-br from-blue-600 to-blue-700"
                  shadowColor="shadow-[0px_10px_15px_-3px_rgba(28,57,142,0.5),0px_4px_6px_-4px_rgba(28,57,142,0.5)]"
                />
                </div>
              </div>
            </div>

            {/* Right Side - Login Card */}
            <div className="w-full max-w-md mx-auto lg:mx-0 flex flex-col gap-5">
              <div className="bg-[rgba(29,41,61,0.9)] relative rounded-[14px] w-full p-6 md:p-8">
                <div
                  aria-hidden="true"
                  className="absolute border-[#314158] border-[0.8px] border-solid inset-0 pointer-events-none rounded-[14px] shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.2)]"
                />
                <div className="flex flex-col gap-6">
                  {/* Card Header */}
                  <div className="space-y-2">
                    <p className="font-['Arial:Regular',sans-serif] leading-[28px] not-italic text-lg md:text-xl text-white">
                      Welcome Back
                    </p>
                    <p className="font-['Arial:Regular',sans-serif] leading-[20px] not-italic text-[#90a1b9] text-sm md:text-base">
                      Sign in to access your PMO suite
                    </p>
                  </div>

                  {/* Card Content */}
                  {mfaChallenge ? (
                  <form onSubmit={handleMfaSubmit} className="space-y-6" aria-label="Two-factor sign-in">
                    <div className="space-y-2">
                      <label htmlFor="mfa-code" className="block text-sm md:text-base text-[#cad5e2]">
                        Enter the 6-digit code from your authenticator app
                      </label>
                      <input
                        id="mfa-code"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        autoFocus
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value)}
                        placeholder="123 456"
                        required
                        className="w-full bg-[rgba(15,23,43,0.5)] h-[44px] rounded-[8px] px-3 text-center text-lg tracking-widest text-white placeholder-[#62748e] border border-[#45556c] focus:border-blue-500 focus:outline-none"
                      />
                      <p className="text-xs text-[#90a1b9]">Lost your phone? Enter one of your backup codes instead.</p>
                    </div>
                    <button
                      type="submit"
                      disabled={loading || mfaCode.trim().length < 6}
                      className="w-full bg-gradient-to-r from-[#fe9a00] h-[44px] rounded-[8px] to-[#e17100] hover:from-[#e58900] hover:to-[#c86600] transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm text-[#0f172b] font-medium"
                    >
                      {loading ? 'Checking…' : 'Verify and sign in'}
                    </button>
                    <button type="button" onClick={() => setMfaChallenge(null)}
                      className="w-full min-h-[44px] text-sm text-[#51a2ff] hover:text-[#73b5ff]">
                      Use a different account
                    </button>
                  </form>
                  ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* SSO Button */}
                    <button
                      type="button"
                      onClick={handleSSOLogin}
                      className="relative bg-gradient-to-r from-[#155dfc] h-[44px] rounded-[8px] shadow-[0px_10px_15px_-3px_rgba(28,57,142,0.3),0px_4px_6px_-4px_rgba(28,57,142,0.3)] to-[#1447e6] w-full hover:from-[#1450e0] hover:to-[#123dd0] transition-all flex items-center justify-center gap-2"
                    >
                      <Shield className="size-4 text-white" />
                      <span className="font-['Arial:Regular',sans-serif] leading-[20px] not-italic text-sm text-white">
                        Sign in with SSO
                      </span>
                    </button>

                    {/* Divider */}
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-[#314158]" />
                      </div>
                      <div className="relative flex justify-center text-xs">
                        <span className="bg-[#1d293d] px-3 text-[#62748e]">
                          Or continue with email
                        </span>
                      </div>
                    </div>

                    {/* Email Field */}
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <label className="block">
                          <span className="font-['Arial:Regular',sans-serif] leading-[20px] not-italic text-sm md:text-base text-[#cad5e2]">
                            Email Address
                          </span>
                        </label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                            <Mail className="size-4 text-[#62748e]" />
                          </div>
                          <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="name@company.com"
                            required
                            className="w-full bg-[rgba(15,23,43,0.5)] h-[40px] rounded-[8px] pl-10 pr-3 py-2 font-['Arial:Regular',sans-serif] text-sm text-white placeholder-[#62748e] border border-[#45556c] focus:border-blue-500 focus:outline-none"
                          />
                        </div>
                      </div>

                      {/* Password Field */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="block">
                            <span className="font-['Arial:Regular',sans-serif] leading-[20px] not-italic text-sm md:text-base text-[#cad5e2]">
                              Password
                            </span>
                          </label>
                          <Link
                            to="/forgot-password"
                            className="font-['Arial:Regular',sans-serif] leading-[16px] not-italic text-xs text-[#51a2ff] hover:text-[#73b5ff] transition-colors"
                          >
                            Forgot password?
                          </Link>
                        </div>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                            <Lock className="size-4 text-[#62748e]" />
                          </div>
                          <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            required
                            className="w-full bg-[rgba(15,23,43,0.5)] h-[40px] rounded-[8px] pl-10 pr-3 py-2 font-['Arial:Regular',sans-serif] text-sm text-white placeholder-[#62748e] border border-[#45556c] focus:border-blue-500 focus:outline-none"
                          />
                        </div>
                      </div>

                      {/* Sign In Button */}
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-[#fe9a00] h-[44px] rounded-[8px] shadow-[0px_10px_15px_-3px_rgba(123,51,6,0.3),0px_4px_6px_-4px_rgba(123,51,6,0.3)] to-[#e17100] hover:from-[#e58900] hover:to-[#c86600] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        <span className="font-['Arial:Regular',sans-serif] leading-[20px] not-italic text-sm text-[#0f172b] font-medium">
                          {loading ? 'Signing in...' : 'Sign In'}
                        </span>
                      </button>
                    </div>

                    {/* Sign up + need-access links */}
                    <div className="pt-4 border-t border-[#314158] space-y-2">
                      {/* Self-serve signup — only meaningful in SaaS mode; for
                          standalone deployments this link just lands on /signup
                          which returns the "Public signup is disabled" message.
                          Cheap to leave in; expensive to gate per-tenant. */}
                      <div className="flex items-center justify-center gap-2 text-sm">
                        <span className="text-[#90a1b9]">New to Karyva?</span>
                        <Link
                          to="/signup"
                          className="text-[#51a2ff] hover:text-[#73b5ff] transition-colors font-medium"
                        >
                          Start free trial
                        </Link>
                      </div>
                      <div className="flex items-center justify-center gap-2 text-sm">
                        <span className="text-[#90a1b9]">Need help?</span>
                        <button
                          type="button"
                          onClick={() => {
                            // Open the user's email client pre-filled
                            // with a help request. No in-app support
                            // surface exists yet; mailto: is a working
                            // fallback that does the right thing.
                            window.location.href = 'mailto:support@karyva.com?subject=Karyva%20%E2%80%94%20Login%20help%20needed'
                          }}
                          className="text-[#51a2ff] hover:text-[#73b5ff] transition-colors"
                        >
                          Contact administrator
                        </button>
                      </div>
                    </div>
                  </form>
                  )}
                </div>
              </div>

              {/* Enterprise Security Badge */}
              <div className="flex items-center justify-center gap-2">
                <Shield className="size-4 text-[#51a2ff]" />
                <p className="font-['Arial:Regular',sans-serif] leading-[20px] not-italic text-sm text-[#62748e]">
                  Enterprise-grade security
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-[rgba(15,23,43,0.5)] border-t border-[#1d293d] py-5 px-4 sm:px-6 lg:px-8 mt-auto relative z-10">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 w-full max-w-7xl mx-auto">
          {/* Left - Logo and Copyright */}
          <div className="flex items-center gap-4">
            <p className="font-['Inter:Medium',sans-serif] font-medium leading-[16px] not-italic text-[#90a1b9] text-base tracking-[1.28px]">
              KARYV
            </p>
            <p className="font-['Arial:Regular',sans-serif] leading-[20px] not-italic text-[#62748e] text-sm">
              © 2025 Karyva. All rights reserved.
            </p>
          </div>

          {/* Right - Footer Links — Sprint 15: were dead buttons.
              Now real anchors to the marketing site. Marketing pages
              are served from website.karyva.com; if the URLs change,
              update this block. */}
          <div className="flex items-center gap-4 sm:gap-6 flex-wrap justify-center">
            <a
              href="https://www.karyva.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="font-['Arial:Regular',sans-serif] leading-[20px] not-italic text-[#62748e] text-sm hover:text-[#90a1b9] transition-colors"
            >
              Privacy Policy
            </a>
            <a
              href="https://www.karyva.com/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="font-['Arial:Regular',sans-serif] leading-[20px] not-italic text-[#62748e] text-sm hover:text-[#90a1b9] transition-colors"
            >
              Terms of Service
            </a>
            <a
              href="mailto:support@karyva.com"
              className="font-['Arial:Regular',sans-serif] leading-[20px] not-italic text-[#62748e] text-sm hover:text-[#90a1b9] transition-colors"
            >
              Support
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

